import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  AddBalanceSnapshotDialog,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CurrencyAmountUI,
  DataTable,
  DateUI,
  DepositCard,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  entryFromEventFragment,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  LabelPill,
  PrimaryFullScreenSpinner,
  TimeLog,
  TransferCard,
  Txt,
  toast,
} from "@repo/ui";
import {
  formatLocalDateComponents,
  NotFoundError,
  neverFunc,
} from "@repo/utils";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { RiArrowLeftFill } from "react-icons/ri";
import { AssetRulesCard } from "~/components/Asset/AssetRulesCard";
import { AssetTransactionsCard } from "~/components/Asset/AssetTransactionsCard";
import { AssetBalanceChart } from "~/components/Asset/BalanceChart";
import { ExternalBankAccountAssetDetailsCard } from "~/components/Asset/ExternalBankAccountAssetDetails";
import { ExternalBankAccountAssetMonthlyInsightsCard } from "~/components/Asset/ExternalBankAccountAssetMonthlyInsightsCard";
import { FixedIncomeFundAssetDetailsCard } from "~/components/Asset/FixedIncomeFundAssetDetails";
import { FXAssetDetailsCard } from "~/components/Asset/FXAssetDetails";
import { PaymentIdentifiersCard } from "~/components/Asset/PaymentIdentifiers";
import { SavingsAssetDetailsCard } from "~/components/Asset/SavingsAssetDetails";
import { TermDepositAssetDetailsCard } from "~/components/Asset/TermDepositAssetDetails";
import { CorpayBeneficiary } from "~/components/CorpayBeneficiary";
import { AdminCurrencycloudBeneficiaryCard } from "~/components/Currencycloud/AdminCurrencycloudBeneficiaryCard";
import { AdminEburyBeneficiaryCard } from "~/components/Ebury/AdminEburyBeneficiaryCard";
import { AssetLedgerAccountCard } from "~/components/Ledger/LedgerAccountCard";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { AdminAssetDetailQuery } from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/assets_/$assetId")({
  head: () => opsHead("Asset"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <AssetDetailPage />
    </Suspense>
  ),
});

const AssetDetailPage = () => {
  const params = useParams({ strict: false });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const navigate = useNavigate();

  const { data, error } = useSuspenseQuery(
    graphql(`
      query AdminAssetDetail($id: ID!) {
        appEnvironment {
          id
          environment
        }
        asset(id: $id) {
          id
          ...CorpayBeneficiary
          class
          country
          currency
          logo {
            id
            slug
            url
          }
          apy
          feeRate
          transactionFeeRate
          name
          openedAt
          status
          sourceDataAsOf
          lastSyncedAt
          settlementAccount {
            ... on FXAsset {
              ...AssetCardFields
            }
            ... on SavingsAsset {
              ...AssetCardFields
            }
            ... on BeneficiaryAccount {
              ...BeneficiaryAssetCardFields
            }
          }
          estimatedValue {
            amount
            currency
          }
          estimatedValueChangeOverMonth {
            amount
            currency
          }
          estimatedValueWithOpenFundMovements {
            amount
            currency
          }
          availableBalance {
            amount
            currency
          }
          openFundMovements {
            ... on Transfer {
              id
              ...TransferCardFields
            }
            ... on Deposit {
              id
              ...DepositCardFields
            }
          }
          assetRules {
            ...AssetRuleCardFields
          }
          events {
            ...TimeLogEventEntry
          }
          assetLedgerAccounts {
            id
            ...AssetLedgerAccountCardFields
            balances {
              id
              unit
              amounts {
                id
                available
                total
                pendingCredits
              }
            }
          }
          businessEntity {
            id
            currencycloudAccount {
              id
            }
            organisation {
              id
              ...OrganisationCardFields
              businessEntities {
                id
                name
                eburyClient {
                  id
                }
                corpayClient {
                  id
                }
                currencycloudAccount {
                  id
                }
              }
              assets {
                id
                status
                ...AssetPickerFields
              }
            }
            ...BusinessEntityCardFields
          }
          custodyAccount {
            id
            shortRef
          }
          ... on FXAsset {
            eburyAccount {
              id
            }
            currencycloudAccount {
              id
            }
          }
          ... @defer {
            eburyBeneficiaries {
              id
              businessEntity {
                id
              }
              ...AdminEburyBeneficiaryCard
            }
          }
          ... @defer {
            currencycloudBeneficiaries {
              id
              businessEntity {
                id
              }
              ...AdminCurrencycloudBeneficiaryCard
            }
          }
          paymentIdentifiers {
            id
            ...PaymentIdentifierComponent
          }
          ... on FixedIncomeFundAsset {
            apiOrdersSupported
            product {
              id
              provider {
                ...ProviderCardFields
              }
              ...ProductCardFields
              ... on FundProduct {
                unit {
                  id
                }
                latestUnitPrice {
                  price
                  targetUnit
                  at
                }
              }
            }
          }
          ... on FXAsset {
            eburyAccount {
              id
              active
              clientId
            }
            currencycloudBalance {
              id
              account {
                id
              }
            }
            currencycloudAccount {
              id
            }
            provider {
              id
              name
              internalName
            }
          }
          ... on ExternalBankAccountAsset {
            id
            details {
              id
              ... on ManualEntryExternalAccountDetails {
                externalBankAccountDataInstitution {
                  id
                  displayName
                  logoUrl
                }
              }
            }
            beneficiaryAccount {
              id
              ...BeneficiaryAssetCardFields
            }
            ...ExternalBankAccountAssetMonthlyInsightsFields
          }
          ... on SavingsAsset {
            product {
              id
              provider {
                ...ProviderCardFields
              }
              ...ProductCardFields
            }
          }
          ... on TermDepositAsset {
            start
            end
            principleAmount {
              amount
            }
            maturityInstructions {
              ... on TermDepositMaturityInstructionsRollover {
                updatedAt
              }
              ... on TermDepositMaturityInstructionsRolloverPrinciple {
                toAsset {
                  id
                  ...AssetCardFields
                }
                updatedAt
              }
              ... on TermDepositMaturityInstructionsTransfer {
                toAsset {
                  id
                  ...AssetCardFields
                }
                updatedAt
              }
            }
            product {
              id
              provider {
                ...ProviderCardFields
              }
              ...ProductCardFields
            }
          }
          ...AssetCardFields
          syncStatusHistory(limit: 10) {
            id
            sourceDataAsOf
            syncedAt
          }
          balanceSnapshotHistory(limit: 10) {
            id
            balance
            unit
            effectiveAt
            createdAt
          }
        }
      }
    `),
    { variables: { id: params.assetId ?? "" } },
  );

  const [deleteAsset, { loading: deleteAssetLoading }] = useMutation(
    graphql(`
      mutation DeleteAsset($input: DeleteAssetInput!) {
        deleteAsset(input: $input) {
          organisation {
            id
          }
        }
      }
    `),
    {
      variables: {
        input: {
          assetId: params.assetId ?? "",
        },
      },
      onCompleted: (data) => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Asset deleted",
        });
        void navigate({
          to: "/organisations/$organisationId",
          params: { organisationId: data.deleteAsset.organisation.id },
        });
      },
      onError: (err) => {
        toast({
          kind: "error",
          autoHide: false,
          title: "Something went wrong deleting asset",
          description: err.message,
        });
        setConfirmDialogOpen(false);
      },
    },
  );
  const asset = data?.asset;
  if (!asset) {
    throw new NotFoundError();
  }

  useOpsTitle(`${asset.businessEntity?.name ?? ""} • ${asset.name}`);

  // An FX asset can't be a beneficiary of its own provider account, but can be
  // synced to a different provider on the same business entity.
  const isEburyAsset =
    asset.__typename === "FXAsset" && asset.eburyAccount !== null;
  const isCurrencycloudAsset =
    asset.__typename === "FXAsset" && asset.currencycloudAccount !== null;

  const fxBeneficiaryTargets = (
    asset.businessEntity?.organisation?.businessEntities ?? []
  ).flatMap((be) => {
    const targets: {
      businessEntity: typeof be;
      provider: "EBURY" | "CURRENCYCLOUD";
    }[] = [];

    const isSelfBusinessEntity = be.id === asset.businessEntity?.id;

    if (be.eburyClient && !(isSelfBusinessEntity && isEburyAsset)) {
      targets.push({ businessEntity: be, provider: "EBURY" });
    }
    if (
      be.currencycloudAccount &&
      !(isSelfBusinessEntity && isCurrencycloudAsset)
    ) {
      targets.push({ businessEntity: be, provider: "CURRENCYCLOUD" });
    }
    return targets;
  });

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`${asset.businessEntity?.name ?? ""} • ${asset.name}`}
        subtitle={asset.id}
        buttons={
          <div className="flex gap-2">
            {data.appEnvironment?.environment !== "PRODUCTION" ? (
              <Link
                to="/assets/$assetId/create-demo-transactions"
                params={{ assetId: asset.id }}
              >
                <Button variant="secondary">Create demo transactions</Button>
              </Link>
            ) : null}
          </div>
        }
      />
      <div className="flex flex-col gap-6">
        <div className="flex flex-row gap-4">
          <div className="flex w-2/3 flex-col gap-6">
            <AssetDetailsCard asset={asset} />
          </div>

          <PaymentIdentifiersCard asset={asset} />
        </div>

        <AssetRulesCard rules={asset.assetRules ?? []} />

        <AssetCurrentBalanceDetails asset={asset} />

        <OpenFundMovementsCard asset={asset} />

        <AssetBalanceChart assetId={asset.id} />

        {asset.__typename === "ExternalBankAccountAsset" && (
          <ExternalBankAccountAssetMonthlyInsightsCard asset={asset} />
        )}

        <AssetTransactionsCard
          assetId={asset.id}
          orgId={asset.businessEntity?.organisation?.id ?? ""}
          assetName={asset.name}
          assetClass={asset.class}
          assetKind={asset.assetKind}
        />

        <div className="flex flex-row gap-4">
          {asset.__typename !== "ExternalBankAccountAsset" &&
            fxBeneficiaryTargets.length > 0 && (
              <Card variant="panel">
                <CardHeader>
                  <CardTitle>
                    <Txt variant="card-title">FX provider beneficiaries</Txt>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <Txt variant="body">
                    Sync this asset to allow it to be used as a beneficiary in
                    the respective Visa or Ebury clients
                  </Txt>

                  {fxBeneficiaryTargets.map(
                    ({ businessEntity: be, provider }) => {
                      switch (provider) {
                        case "EBURY": {
                          // apollo defer returns undefined while loading
                          const eburyBeneficiary =
                            asset.eburyBeneficiaries === undefined
                              ? undefined
                              : (asset.eburyBeneficiaries?.find(
                                  (b) => b.businessEntity?.id === be.id,
                                ) ?? null);
                          return (
                            <div
                              key={`${be.id}_${provider}`}
                              className="flex flex-col gap-2"
                            >
                              <div className="font-semibold text-sm">
                                {be.name} (Ebury {be.eburyClient?.id})
                              </div>

                              <AdminEburyBeneficiaryCard
                                eburyBeneficiary={eburyBeneficiary}
                                eburyBusinessEntityId={be.id}
                                assetId={asset.id}
                                errors={error?.graphQLErrors?.filter(
                                  (gqlError) => {
                                    return (
                                      gqlError.path?.at(0) === "asset" &&
                                      gqlError.path?.at(1) ===
                                        "eburyBeneficiaries"
                                    );
                                  },
                                )}
                              />
                              <hr />
                            </div>
                          );
                        }
                        case "CURRENCYCLOUD": {
                          const currencycloudBeneficiary =
                            asset.currencycloudBeneficiaries === undefined
                              ? undefined
                              : (asset.currencycloudBeneficiaries?.find(
                                  (b) => b.businessEntity?.id === be.id,
                                ) ?? null);

                          return (
                            <div
                              key={`${be.id}_${provider}`}
                              className="flex flex-col gap-2"
                            >
                              <div className="font-semibold text-sm">
                                {be.name} (Visa {be.currencycloudAccount?.id})
                              </div>

                              <AdminCurrencycloudBeneficiaryCard
                                currencycloudBeneficiary={
                                  currencycloudBeneficiary
                                }
                                currencycloudBusinessEntityId={be.id}
                                assetId={asset.id}
                                errors={error?.graphQLErrors?.filter(
                                  (gqlError) => {
                                    return (
                                      gqlError.path?.at(0) === "asset" &&
                                      gqlError.path?.at(1) ===
                                        "currencycloudBeneficiaries"
                                    );
                                  },
                                )}
                              />
                              <hr />
                            </div>
                          );
                        }
                        default:
                          return neverFunc(provider);
                      }
                    },
                  )}
                </CardContent>
              </Card>
            )}

          <Card className="flex-grow" variant="panel">
            <CardHeader>
              <CardTitle>
                <Txt variant="card-title">Ledger Accounts</Txt>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {asset.assetLedgerAccounts?.map((assetLedgerAccount) => (
                <AssetLedgerAccountCard
                  {...assetLedgerAccount}
                  key={assetLedgerAccount.id}
                />
              ))}
            </CardContent>
          </Card>
        </div>
        <div className="flex flex-row justify-end">
          {asset.assetKind === "PRIMARY_MANAGED" && (
            <Dialog
              open={confirmDialogOpen}
              onOpenChange={setConfirmDialogOpen}
            >
              <DialogTrigger asChild>
                <Button
                  onClick={() => setConfirmDialogOpen(true)}
                  variant="destructive"
                >
                  Delete asset
                </Button>
              </DialogTrigger>

              <DialogContent hideCloseButton>
                <DialogHeader>
                  <DialogTitle>Permenantly delete {asset.name}</DialogTitle>
                  <DialogDescription>
                    <strong>ATTENTION</strong>
                    <br /> This will permenantly delete this asset from the
                    database. It can not be recovered after this action has been
                    performed.
                  </DialogDescription>
                </DialogHeader>

                <DialogFooter className="gap-2 sm:justify-between">
                  <Button
                    leftIcon={<RiArrowLeftFill />}
                    className="px-6"
                    size="lg"
                    variant="secondary"
                    onClick={(e) => {
                      setConfirmDialogOpen(false);
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    {" "}
                    Back
                  </Button>

                  <div className="flex flex-row items-center gap-x-4">
                    <Button
                      variant="destructive"
                      className="px-6"
                      size="lg"
                      loading={deleteAssetLoading}
                      onClick={async (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        deleteAsset();
                      }}
                    >
                      Confirm deletion
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <CorpayBeneficiary account={asset} />

        {asset.syncStatusHistory && asset.syncStatusHistory.length > 0 && (
          <Card variant="panel">
            <CardHeader>
              <CardTitle>
                <Txt variant="card-title">Sync status history</Txt>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                data={asset.syncStatusHistory}
                columns={[
                  {
                    id: "number",
                    header: "#",
                    cell: ({ row }) => row.index + 1,
                  },
                  {
                    id: "sourceDataAsOf",
                    header: "Source data as of",
                    cell: ({ row }) =>
                      formatLocalDateComponents(row.original.sourceDataAsOf)
                        .datetimeTz,
                  },
                  {
                    id: "syncedAt",
                    header: "Synced at",
                    cell: ({ row }) =>
                      formatLocalDateComponents(row.original.syncedAt)
                        .datetimeTz,
                  },
                ]}
                hidePagination
                pageSize={10_000}
                className="overflow-hidden"
              />
            </CardContent>
          </Card>
        )}

        {asset.balanceSnapshotHistory &&
          asset.balanceSnapshotHistory.length > 0 && (
            <Card variant="panel">
              <CardHeader>
                <CardTitle>
                  <Txt variant="card-title">Balance snapshots</Txt>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  data={asset.balanceSnapshotHistory}
                  columns={[
                    {
                      id: "number",
                      header: "#",
                      cell: ({ row }) => row.index + 1,
                    },
                    {
                      id: "balance",
                      header: "Balance",
                      cell: ({ row }) => row.original.balance.toFixed(2),
                    },
                    {
                      id: "unit",
                      header: "Unit",
                      cell: ({ row }) => row.original.unit,
                    },
                    {
                      id: "effectiveAt",
                      header: "Effective at",
                      cell: ({ row }) =>
                        formatLocalDateComponents(row.original.effectiveAt)
                          .datetimeTz,
                    },
                    {
                      id: "createdAt",
                      header: "Created at",
                      cell: ({ row }) =>
                        formatLocalDateComponents(row.original.createdAt)
                          .datetimeTz,
                    },
                  ]}
                  hidePagination
                  pageSize={10_000}
                  className="overflow-hidden"
                />
              </CardContent>
            </Card>
          )}

        <Card variant="panel">
          <CardHeader>
            <CardTitle>
              <Txt variant="card-title">Event log</Txt>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {asset.events && (
              <TimeLog entries={asset.events.map(entryFromEventFragment)} />
            )}
            {(asset.events?.length ?? 0) === 0 && (
              <div className="text-center">No events</div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminPageContainer>
  );
};

function AssetCurrentBalanceDetails({
  asset,
}: {
  asset: NonNullable<AdminAssetDetailQuery["asset"]>;
}) {
  const [snapshotOpen, setSnapshotOpen] = useState(false);

  const isManualEntry =
    asset.__typename === "ExternalBankAccountAsset" &&
    asset.details?.__typename === "ManualEntryExternalAccountDetails";

  const externalAccountId =
    asset.__typename === "ExternalBankAccountAsset"
      ? (asset.details?.id ?? null)
      : null;

  const balanceLedgerAccount = asset.assetLedgerAccounts?.find(
    (assetLedgerAccount) =>
      assetLedgerAccount.assetLedgerAccountKind === "BALANCE",
  );

  return (
    <>
      <Card variant="panel">
        <CardContent className="grid grid-cols-4 justify-between">
          <div className="flex flex-col gap-1">
            <Txt variant="detail-label">Current balance</Txt>
            <Txt variant="metric">
              <CurrencyAmountUI
                amount={asset.estimatedValue?.amount}
                currency={asset.estimatedValue?.currency}
              />
            </Txt>
          </div>

          <div className="flex flex-col gap-1">
            <Txt variant="detail-label">Available balance</Txt>
            <Txt variant="metric">
              <CurrencyAmountUI
                amount={asset.availableBalance?.amount}
                currency={asset.availableBalance?.currency}
              />
            </Txt>
          </div>

          <div className="flex flex-col gap-1">
            <Txt variant="detail-label">
              Balance (including open fund movements)
            </Txt>
            <Txt variant="metric">
              <CurrencyAmountUI
                amount={asset.estimatedValueWithOpenFundMovements?.amount}
                currency={asset.estimatedValueWithOpenFundMovements?.currency}
              />
            </Txt>
          </div>

          <div className="flex flex-col gap-1">
            <Txt variant="detail-label">Change 1 month</Txt>
            <Txt variant="metric">
              <CurrencyAmountUI
                amount={asset.estimatedValueChangeOverMonth?.amount}
                currency={asset.estimatedValueChangeOverMonth?.currency}
                showSign
              />
            </Txt>
          </div>

          {asset.__typename === "FixedIncomeFundAsset" &&
            asset.product?.__typename === "FundProduct" &&
            asset.product.latestUnitPrice && (
              <div className="flex flex-col gap-1">
                <Txt variant="detail-label">
                  Latest unit price (
                  <DateUI date={asset.product.latestUnitPrice.at} />)
                </Txt>
                <Txt variant="metric">
                  {asset.product.latestUnitPrice.price
                    .toNumber()
                    .toLocaleString(undefined, {
                      minimumFractionDigits: 4,
                      maximumFractionDigits: 4,
                    })}{" "}
                  {asset.product.latestUnitPrice.targetUnit}
                </Txt>
              </div>
            )}

          {balanceLedgerAccount?.balances?.map((balance) => {
            const isProductUnit =
              asset.__typename === "FixedIncomeFundAsset" &&
              asset.product?.__typename === "FundProduct"
                ? asset.product.unit?.id === balance.unit
                : false;

            const unitLabel = isProductUnit ? "unit" : balance.unit;

            return (
              <div className="flex flex-col gap-1" key={balance.id}>
                <Txt variant="detail-label">Ledger {unitLabel} balance</Txt>
                <div className="flex items-center gap-2">
                  <Txt variant="metric">
                    {balance.amounts.total
                      .toNumber()
                      .toLocaleString(undefined, {
                        minimumFractionDigits: 4,
                        maximumFractionDigits: 4,
                      })}
                  </Txt>
                  {asset.__typename === "FixedIncomeFundAsset" &&
                    !isProductUnit && (
                      <HoverCard>
                        <HoverCardTrigger>
                          <LabelPill variant="warning" label="Warning" />
                        </HoverCardTrigger>
                        <HoverCardContent side="top" className="bg-black">
                          <Txt inverseTone>
                            This fund account holds a balance not in the fund
                            unit
                          </Txt>
                        </HoverCardContent>
                      </HoverCard>
                    )}
                </div>
              </div>
            );
          })}

          <div className="flex flex-col gap-1">
            <Txt variant="detail-label">Customer preview</Txt>
            <AccountCard account={asset} />
          </div>

          {isManualEntry && (
            <div className="flex flex-col gap-1">
              <Txt variant="detail-label">Add manual entry</Txt>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setSnapshotOpen(true)}
              >
                Add balance snapshot
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {externalAccountId && (
        <AddBalanceSnapshotDialog
          open={snapshotOpen}
          onOpenChange={setSnapshotOpen}
          externalAccountId={externalAccountId}
          currency={asset.currency}
        />
      )}
    </>
  );
}

function OpenFundMovementsCard({
  asset,
}: {
  asset: NonNullable<AdminAssetDetailQuery["asset"]>;
}) {
  const openFundMovements = asset.openFundMovements ?? [];

  const fundMovementCards = openFundMovements.flatMap((fundMovement) => {
    switch (fundMovement.__typename) {
      case "Transfer": {
        const isTransferOut = fundMovement.sourceAsset?.id === asset.id;
        const isTransferIn =
          fundMovement.destinationAccount?.__typename !==
            "BeneficiaryAccount" &&
          fundMovement.destinationAccount?.id === asset.id;
        if (!isTransferIn && !isTransferOut) {
          return [];
        }
        let variant: "TRANSFER_IN" | "TRANSFER_OUT";
        if (isTransferOut && isTransferIn) {
          variant = "TRANSFER_OUT";
        } else if (isTransferOut) {
          variant = "TRANSFER_OUT";
        } else {
          variant = "TRANSFER_IN";
        }
        return [
          <div key={`${fundMovement.__typename}-${fundMovement.id}`}>
            <Link to={`/transfers/${fundMovement.id}`}>
              <TransferCard
                transfer={fundMovement}
                variant={variant}
                subText={["DATE", "PAYMENT_REFERENCE", "STATUS"]}
              />
            </Link>
          </div>,
        ];
      }
      case "Deposit":
        return [
          <div key={`${fundMovement.__typename}-${fundMovement.id}`}>
            <Link to={`/deposits/${fundMovement.id}`}>
              <DepositCard deposit={fundMovement} />,
            </Link>
          </div>,
        ];
      case "EburyFXTrade":
      case "FundUnitOrder":
        return [];
      default:
        return neverFunc(fundMovement);
    }
  });

  return (
    <Card variant="panel">
      <CardHeader>
        <CardTitle>
          <Txt variant="card-title">Open fund movements</Txt>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {fundMovementCards.length > 0 ? (
          fundMovementCards
        ) : (
          <Txt variant="body">No open fund movements</Txt>
        )}
      </CardContent>
    </Card>
  );
}

function AssetDetailsCard({
  asset,
  className,
}: {
  asset: NonNullable<AdminAssetDetailQuery["asset"]>;
  className?: string;
}) {
  switch (asset.__typename) {
    case "ExternalBankAccountAsset":
      return (
        <ExternalBankAccountAssetDetailsCard
          asset={asset}
          className={className}
        />
      );
    case "FXAsset":
      return <FXAssetDetailsCard asset={asset} className={className} />;
    case "FixedIncomeFundAsset":
      return (
        <FixedIncomeFundAssetDetailsCard asset={asset} className={className} />
      );
    case "SavingsAsset":
      return <SavingsAssetDetailsCard asset={asset} className={className} />;
    case "TermDepositAsset":
      return (
        <TermDepositAssetDetailsCard asset={asset} className={className} />
      );
  }
}
